import * as XLSX from "xlsx";

/**
 * La plantilla de Excel para registrar los ejemplares de un Libro a tu clase
 * de una vez, en vez de uno por uno: una fila por estudiante, y el auxiliar
 * llena el codigo de barras y el titulo del libro que le presta.
 */

const COLUMNAS = {
  IDENTIFICACION: "Identificación",
  ESTUDIANTE: "Estudiante",
  CODIGO: "Código de barras",
  TITULO: "Título",
};
const ORDEN = [COLUMNAS.IDENTIFICACION, COLUMNAS.ESTUDIANTE, COLUMNAS.CODIGO, COLUMNAS.TITULO];

/** Los estudiantes que aun no tienen un ejemplar prestado. */
export const estudiantesSinEjemplar = (estudiantes, ejemplares) => {
  const conEjemplar = new Set(
    ejemplares.filter(({ estudiante }) => estudiante).map(({ estudiante }) => estudiante.id),
  );
  return estudiantes.filter(({ id }) => !conEjemplar.has(id));
};

/**
 * Descarga la plantilla con los estudiantes que aun no tienen ejemplar. Una
 * segunda hoja trae los titulos que pidio el docente, para copiarlos.
 *
 * El codigo de barras va con formato de texto: si no, Excel lo vuelve numero
 * y se pierden los ceros de la izquierda.
 */
export const descargarPlantillaEjemplares = (idSolicitud, estudiantes, tematicas) => {
  const filas = [
    ORDEN,
    ...estudiantes.map(({ identificacion, nombre }) => [identificacion, nombre, "", ""]),
  ];
  const hoja = XLSX.utils.aoa_to_sheet(filas);
  estudiantes.forEach((_estudiante, i) => {
    // Fila i + 2: la 1 es el encabezado. Columna C: el codigo de barras.
    hoja[`C${i + 2}`] = { t: "s", v: "", z: "@" };
    // La identificacion tambien como texto, tal cual viene.
    hoja[`A${i + 2}`] = { t: "s", v: String(estudiantes[i].identificacion ?? ""), z: "@" };
  });
  hoja["!cols"] = [{ wch: 16 }, { wch: 40 }, { wch: 20 }, { wch: 45 }];

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, "Ejemplares");
  XLSX.utils.book_append_sheet(
    libro,
    XLSX.utils.aoa_to_sheet([["Títulos que pidió el docente"], ...tematicas.map((t) => [t])]),
    "Títulos pedidos",
  );
  XLSX.writeFile(libro, `Ejemplares solicitud ${idSolicitud}.xlsx`);
};

const leerArchivo = (archivo) =>
  new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = ({ target }) => resolve(target.result);
    lector.onerror = () => reject(new Error("No fue posible leer el archivo."));
    lector.readAsArrayBuffer(archivo);
  });

/**
 * Lee la plantilla llena. Resuelve { ejemplares, errores }:
 *   - ejemplares: [{ fila, codigo_barras, titulo, estudiante }] para el
 *     backend; `estudiante` es el id en la solicitud, o null si la fila no
 *     trae identificacion (queda sin asignar).
 *   - errores: lo que hay que corregir antes de enviar, con su fila.
 * Las filas sin codigo ni titulo se ignoran: son estudiantes a los que no se
 * les presta nada.
 */
export const leerPlantillaEjemplares = async (archivo, estudiantes) => {
  let libro;
  try {
    libro = XLSX.read(await leerArchivo(archivo), { type: "array" });
  } catch (error) {
    return { ejemplares: [], errores: ["El archivo no es un Excel válido."] };
  }
  const hoja = libro.Sheets[libro.SheetNames[0]];
  // raw: false trae el texto que se ve en la celda, no el numero.
  const filas = XLSX.utils.sheet_to_json(hoja, { raw: false, defval: "" });

  const faltan = [COLUMNAS.CODIGO, COLUMNAS.TITULO].filter(
    (columna) => filas.length > 0 && !(columna in filas[0]),
  );
  if (faltan.length) {
    return {
      ejemplares: [],
      errores: [`Al archivo le falta la columna ${faltan.join(" y ")}. Usa la plantilla.`],
    };
  }

  const porIdentificacion = new Map(
    estudiantes.map((estudiante) => [String(estudiante.identificacion).trim(), estudiante]),
  );
  const ejemplares = [];
  const errores = [];
  filas.forEach((fila, i) => {
    const numero = i + 2;
    const codigo = String(fila[COLUMNAS.CODIGO] ?? "").trim();
    const titulo = String(fila[COLUMNAS.TITULO] ?? "").trim();
    if (!codigo && !titulo) return;
    if (!codigo || !titulo) {
      errores.push(`Fila ${numero}: falta el ${codigo ? "título" : "código de barras"}.`);
      return;
    }
    const identificacion = String(fila[COLUMNAS.IDENTIFICACION] ?? "").trim();
    const estudiante = identificacion ? porIdentificacion.get(identificacion) : null;
    if (identificacion && !estudiante) {
      errores.push(`Fila ${numero}: ${identificacion} no es estudiante de esta solicitud.`);
      return;
    }
    ejemplares.push({ fila: numero, codigo_barras: codigo, titulo, estudiante: estudiante?.id ?? null });
  });

  if (!errores.length && !ejemplares.length) {
    errores.push("El archivo no trae ningún ejemplar con código de barras y título.");
  }
  return { ejemplares, errores };
};
